import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import SupportThread from '@/components/SupportThread';
import { Loader2, CheckCircle, ChevronLeft } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { supabase } from '@/lib/safeSupabase';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { sanitizeError } from '@/lib/errorMessages';
import { useMySupportTickets } from '@/hooks/useMySupportTickets';
import {
  isUnreadReply, loadSupportEmailOptIn, loadSupportMessages, markSupportRepliesSeen,
  postSupportMessage, setSupportEmailOptIn,
} from '@/lib/supportTickets';
import { OTHER_SECTION, SECTION_DETAIL_MAX, SUPPORT_CATEGORIES, SUPPORT_SECTIONS, supportCategoryLabel, supportSectionLabel } from '@/lib/supportFields';


interface SupportFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_LABEL: Record<string, string> = { open: 'Open', in_progress: 'In progress', resolved: 'Resolved' };

export default function SupportForm({ open, onOpenChange }: SupportFormProps) {
  const { user, org } = useAuth();
  const queryClient = useQueryClient();
  const { tickets, unread, effectiveUserId, isImpersonating } = useMySupportTickets();
  const [tab, setTab] = useState<'new' | 'mine'>('new');

  // Opening Contact Support with an unread answer goes straight to it.
  useEffect(() => {
    if (open) setTab(unread.length > 0 ? 'mine' : 'new');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Looking at My requests marks the answers there as read.
  useEffect(() => {
    if (!open || tab !== 'mine' || unread.length === 0) return;
    markSupportRepliesSeen()
      .then(() => queryClient.invalidateQueries({ queryKey: ['my-support-tickets', effectiveUserId] }))
      .catch(() => { /* the dot stays; nothing else depends on it */ });
  }, [open, tab, unread.length, effectiveUserId, queryClient]);
  // One conversation open at a time; the list is the way back.
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);
  const openTicket = tickets.find(t => t.id === openTicketId) ?? null;
  useEffect(() => { if (!open) setOpenTicketId(null); }, [open]);

  const { data: messages = [] } = useQuery({
    queryKey: ['support-messages', effectiveUserId, openTicketId],
    queryFn: () => loadSupportMessages([openTicketId as string]),
    enabled: open && !!openTicketId && !!effectiveUserId,
    refetchInterval: 30_000,
  });

  const sendMessage = async (text: string) => {
    if (!openTicketId) return false;
    try {
      const { saved } = await postSupportMessage(openTicketId, text);
      if (!saved) {
        toast.info('Not sent', { description: 'Nothing is sent while viewing as someone else.' });
        return false;
      }
      queryClient.invalidateQueries({ queryKey: ['support-messages', effectiveUserId, openTicketId] });
      queryClient.invalidateQueries({ queryKey: ['my-support-tickets', effectiveUserId] });
      return true;
    } catch (e) {
      toast.error('Message not sent', { description: e instanceof Error ? e.message : 'Try again.' });
      return false;
    }
  };

  // "Email me when support replies": off until the person turns it on.
  const optInKey = ['support-email-opt-in', effectiveUserId];
  const { data: optIn = false } = useQuery({
    queryKey: optInKey,
    queryFn: () => loadSupportEmailOptIn(effectiveUserId as string),
    enabled: open && !!effectiveUserId,
  });
  const toggleOptIn = async (on: boolean) => {
    if (!effectiveUserId) return;
    queryClient.setQueryData(optInKey, on);
    try {
      await setSupportEmailOptIn(effectiveUserId, on);
      toast.success(on ? "We'll email you when support replies" : 'Support emails turned off');
    } catch (e) {
      queryClient.setQueryData(optInKey, !on);
      toast.error('Setting not saved', { description: sanitizeError(e) });
    }
  };

  const [category, setCategory] = useState('question');
  const [section, setSection] = useState('');
  const [sectionDetail, setSectionDetail] = useState('');
  // "Something else" needs a few words, or the ticket can't be grouped at all.
  const sectionReady = !!section && (section !== OTHER_SECTION || !!sectionDetail.trim());
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    if (!sectionReady || !subject.trim() || !message.trim() || !user) return;
    setSending(true);
    try {
      // Insert the ticket
      const { data: ticket, error } = await supabase.from('support_tickets' as any).insert({
        user_id: user.id,
        org_id: org?.id ?? null,
        category,
        section,
        section_detail: section === OTHER_SECTION ? sectionDetail.trim() : null,
        subject: subject.trim(),
        message: message.trim(),
      } as any).select('id').single();
      if (error) throw error;

      // Email it to the support inbox. Fire-and-forget: the ticket is saved and
      // in the Admin Panel whether or not the email goes out.
      const ticketId = (ticket as { id?: string } | null)?.id;
      if (ticketId) {
        supabase.functions
          .invoke('notify-support', { body: { ticket_id: ticketId } })
          .catch((notifyErr) => console.warn('[SupportForm] Support email failed (non-blocking):', notifyErr));
      }

      // Auto-enrichment for bug reports: attach recent errors & enable diagnostics
      if (category === 'bug') {
        // Fire-and-forget: don't block the user on enrichment
        (async () => {
          try {
            // Fetch user's recent errors (last 24h)
            const since = new Date(Date.now() - 86400000).toISOString();
            const { data: recentErrors } = await supabase
              .from('error_log')
              .select('id')
              .eq('user_id', user.id)
              .gte('created_at', since)
              .order('created_at', { ascending: false })
              .limit(10);

            // Auto-enable diagnostic mode for this user
            await supabase.rpc('toggle_diagnostic_mode', { p_user_id: user.id, p_enable: true });

            console.log('[SupportForm] Bug ticket enrichment: found', recentErrors?.length ?? 0, 'recent errors, diagnostic mode enabled');
          } catch (enrichErr) {
            console.warn('[SupportForm] Enrichment failed (non-blocking):', enrichErr);
          }
        })();
      }

      queryClient.invalidateQueries({ queryKey: ['my-support-tickets', effectiveUserId] });
      setSent(true);
      toast.success('Support request sent', { description: "We'll get back to you as soon as possible." });
    } catch (e: any) {
      toast.error('Error', { description: sanitizeError(e) });
    } finally {
      setSending(false);
    }
  };

  const handleClose = (v: boolean) => {
    if (!v) {
      // Reset form on close
      setTimeout(() => {
        setCategory('question');
        setSection('');
        setSectionDetail('');
        setSubject('');
        setMessage('');
        setSent(false);
      }, 300);
    }
    onOpenChange(v);
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="sm:max-w-md flex flex-col overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Contact Support</SheetTitle>
          <SheetDescription>
            Send a request, or talk with us about one under My requests.
          </SheetDescription>
        </SheetHeader>

        <Tabs value={tab} onValueChange={v => setTab(v as 'new' | 'mine')} className="flex-1 flex flex-col mt-4">
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="new">New request</TabsTrigger>
            <TabsTrigger value="mine" data-testid="support-my-requests-tab">
              My requests
              {unread.length > 0 && <span className="ml-1.5 h-2 w-2 rounded-full bg-primary inline-block" aria-label="New reply" />}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="mine" className="flex-1 space-y-3 py-4">
            <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
              <div>
                <Label htmlFor="support-email-opt-in" className="text-sm">Email me when support replies</Label>
                <p className="text-xs text-muted-foreground">Off by default. The email only says there's a reply; you read it here.</p>
              </div>
              <Switch
                id="support-email-opt-in"
                checked={optIn}
                disabled={!effectiveUserId || isImpersonating}
                onCheckedChange={toggleOptIn}
              />
            </div>

            {openTicket ? (
              <div className="space-y-3" data-testid="support-conversation">
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1" onClick={() => setOpenTicketId(null)}>
                  <ChevronLeft className="h-3 w-3" /> All requests
                </Button>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium leading-snug">{openTicket.subject}</p>
                  <Badge variant={openTicket.status === 'resolved' ? 'outline' : 'secondary'} className="text-[10px] shrink-0">
                    {STATUS_LABEL[openTicket.status] ?? openTicket.status}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">{supportCategoryLabel(openTicket.category)} · {supportSectionLabel(openTicket.section, openTicket.section_detail)}</p>
                <div className="rounded-lg p-2.5 text-sm whitespace-pre-wrap bg-muted/50 ml-6">
                  <p className="text-[11px] font-semibold text-muted-foreground mb-1">
                    You · {formatDistanceToNow(new Date(openTicket.created_at), { addSuffix: true })}
                  </p>
                  {openTicket.message}
                </div>
                <SupportThread viewer="user" messages={messages} onSend={sendMessage} placeholder="Write back to support…" />
              </div>
            ) : tickets.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">You haven't sent any support requests yet.</p>
            ) : tickets.map(t => (
              <button
                type="button"
                key={t.id}
                onClick={() => setOpenTicketId(t.id)}
                className="w-full text-left rounded-lg border p-3 space-y-1 hover:bg-accent/30 transition-colors"
                data-testid="support-ticket"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium leading-snug">{t.subject}</p>
                  <Badge variant={t.status === 'resolved' ? 'outline' : 'secondary'} className="text-[10px] shrink-0">
                    {STATUS_LABEL[t.status] ?? t.status}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground" data-testid="support-ticket-kind">
                  {supportCategoryLabel(t.category)} · {supportSectionLabel(t.section, t.section_detail)}
                </p>
                <p className="text-xs text-muted-foreground">
                  Sent {formatDistanceToNow(new Date(t.created_at), { addSuffix: true })}
                  {' · '}
                  {isUnreadReply(t)
                    ? <span className="font-semibold text-primary">New reply from support</span>
                    : t.last_support_message_at ? 'Support replied' : 'No reply yet'}
                </p>
              </button>
            ))}
          </TabsContent>

          <TabsContent value="new" className="flex-1 flex flex-col">
        {sent ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 py-12">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-primary" />
            </div>
            <div className="text-center space-y-1">
              <p className="font-semibold">Request submitted</p>
              <p className="text-sm text-muted-foreground">We'll review your ticket and respond soon.</p>
            </div>
            <Button variant="outline" onClick={() => handleClose(false)}>Close</Button>
          </div>
        ) : (
          <div className="space-y-4 py-4 flex-1">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="support-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SUPPORT_CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Section <span className="text-destructive">*</span></Label>
              <Select value={section} onValueChange={setSection}>
                <SelectTrigger data-testid="support-section"><SelectValue placeholder="Which part of the app is this about?" /></SelectTrigger>
                <SelectContent>
                  {SUPPORT_SECTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {section === OTHER_SECTION && (
                <Input
                  value={sectionDetail}
                  onChange={e => setSectionDetail(e.target.value)}
                  placeholder="What is it about?"
                  maxLength={SECTION_DETAIL_MAX}
                  data-testid="support-section-detail"
                />
              )}
            </div>
            <div className="space-y-2">
              <Label>Subject <span className="text-destructive">*</span></Label>
              <Input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Brief description of your issue"
                maxLength={200}
              />
            </div>
            <div className="space-y-2">
              <Label>Message <span className="text-destructive">*</span></Label>
              <Textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Tell us more about what's going on…"
                rows={6}
                maxLength={2000}
              />
              <p className="text-xs text-muted-foreground text-right">{message.length}/2000</p>
            </div>
          </div>
        )}

        {!sent && (
          <SheetFooter>
            <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={sending || !sectionReady || !subject.trim() || !message.trim()}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Submit
            </Button>
          </SheetFooter>
        )}
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
